import React, { useState } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useGridInputs from '../../hooks/useGridInputs';
import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getAllFacetsWithSelectedUrlFilters,
  getFacetsToRenderByGridType,
  groupSizeFacets,
} from '../../utils/filters';
import {
  getNewQueryParamString,
  QueryParamBehavior,
} from '../../utils/routing';

import MobileGridFilterRenderer from './MobileGridFilterRenderer';
import MobileGridSelectedFilters from './MobileGridSelectedFilters';

const MobileGridFiltersPaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
`;

const CloseButtonFlexWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CloseButton = styled.button`
  position: relative;
  background-color: white;
  border-radius: 0.375rem;
  color: ${({ theme }) => theme.colors.closeButton};
  font-size: 15px;
  vertical-align: middle;
  font-weight: 300;
  padding: 24px 24px 0px 0px;
  font-family: inherit;
  border: none;
  cursor: pointer;
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const FacetList = styled.ul.attrs(() => ({
  id: 'facet-scrollable',
}))`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.colors.gridFacetListBorderColor};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  height: 100%;
`;

const FacetItem = styled.li`
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.gridFacetListBorderColor};
  &:last-child {
    border-bottom: none;
  }
`;

const ApplyButtonStaticFiltersPaneFooter = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: 2px 1rem 1rem 1rem;
  background: white;

  &::before {
    content: '';
    position: absolute;
    top: -16px;
    left: 0;
    right: 0;
    height: 16px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), white);
    pointer-events: none;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  min-height: 48px;
  color: white;
  font-weight: bold;
  letter-spacing: normal;
  border-radius: 0.375rem;
  border: none;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-family: inherit;
  font-size: 16px;
  ${({ theme }) => theme.gridFilterStyles.applyFiltersButtonStyle};
`;

const MobileGridFiltersPane = ({ handleClose = () => {} }) => {
  const router = useRouter();
  const { selectedFacetsToApply } = useSelectedFacetsToApply();
  const { facets, selectedUrlFilters, allRegisteredFacets } =
    useUnifiedProductGridData();
  const { gridType } = useGridInputs();
  const [openPanels, setOpenPanels] = useState({});

  const selectedFacets = getAllFacetsWithSelectedUrlFilters({
    selectedUrlFilters,
    allRegisteredFacets,
    facets,
    gridType,
  });

  const facetsWithSizesGrouped = groupSizeFacets(selectedFacets);

  const facetsToRender = getFacetsToRenderByGridType({
    gridType,
    facets: facetsWithSizesGrouped,
  });

  const handleApplyFilters = () => {
    const [aspathname, query] = router.asPath.split('?');
    const newQueryParamString = getNewQueryParamString({
      query,
      newQueryParams: selectedFacetsToApply,
      queryParamBehavior: QueryParamBehavior.REPLACE,
    });

    if (!newQueryParamString) {
      router.replace(router.pathname, aspathname, {
        shallow: true,
      });
      handleClose();
      return;
    }

    router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
      shallow: true,
    });

    handleClose();
  };

  const togglePanel = (panelId) => {
    setOpenPanels((prevState) => ({
      ...prevState,
      [panelId]: !prevState[panelId],
    }));
  };

  const closeAllPanels = () => {
    setOpenPanels({});
  };

  return (
    <MobileGridFiltersPaneWrapper>
      <CloseButtonFlexWrapper>
        <CloseButton type="button" onClick={handleClose}>
          <SrOnly>Close panel</SrOnly>
          <svg
            viewBox="0 0 10 10"
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            strokeWidth="1"
            aria-labelledby="Close panel"
          >
            <line
              stroke="currentColor"
              strokeWidth="1"
              x1="0"
              x2="10"
              y1="10"
              y2="0"
            />
            <line
              stroke="currentColor"
              strokeWidth="1"
              x1="0"
              x2="10"
              y1="0"
              y2="10"
            />
          </svg>
        </CloseButton>
      </CloseButtonFlexWrapper>

      <MobileGridSelectedFilters closeAllPanels={closeAllPanels} />
      <FacetList>
        {facetsToRender?.map((facet) => (
          <FacetItem key={facet?.name || facet?.display_name}>
            <MobileGridFilterRenderer
              facet={facet}
              togglePanel={togglePanel}
              openPanels={openPanels}
            />
          </FacetItem>
        ))}
      </FacetList>
      <ApplyButtonStaticFiltersPaneFooter>
        <ApplyButton
          type="button"
          disabled={!Object.keys(selectedFacetsToApply)?.length} // Disable if no filters selected
          onClick={handleApplyFilters}
          data-autotag="filter-apply"
        >
          <FormattedMessage
            id="site_product_grid.apply_filters"
            defaultMessage="Apply Filters"
          />
        </ApplyButton>
      </ApplyButtonStaticFiltersPaneFooter>
    </MobileGridFiltersPaneWrapper>
  );
};

MobileGridFiltersPane.propTypes = {
  handleClose: PropTypes.func,
};

export default MobileGridFiltersPane;
